import { useEffect, useState } from 'react';
import { logo } from '../lib/logo';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CoachGradeCard } from '../components/CoachGradeCard';
import { CoachTrendChart } from '../components/CoachTrend';
import { TendencyChart } from '../components/Tendencies';
import { DataTable, Stat } from '../components/DataTable';
import { Disclosure } from '../components/Disclosure';
import { ErrorLine } from '../components/ErrorLine';
import { LoadingLine } from '../components/LoadingLine';
import { api, type CoachTrend, type League, type PhaseCard, type SeasonReport, type Tendencies } from '../lib/api';
import { teamColor } from '../lib/colors';

// Only game management is the head coach's own call. Public data does not name
// coordinators, so the other phases are the team's and are labelled that way
// rather than left to read as his.
const PHASES: { id: keyof SeasonReport['phases']; label: string; what: string }[] = [
  { id: 'head_coach', label: 'Game management', what: 'His own calls: fourth downs, two-point tries, kicks, timeouts' },
  { id: 'offense', label: 'Play calling', what: 'Run or pass in key situations, offensive staff' },
  { id: 'defense', label: 'Defense', what: 'Coverage and pass rush, defensive staff' },
  { id: 'special_teams', label: 'Special teams', what: 'Field goal attempts and kickoffs, special teams staff' },
];

function Card({ c, title, what }: { c: PhaseCard; title: string; what: string }) {
  const playcalls = c.wp_decisions === 0 && c.playcall_decisions > 0;
  // Per game, not per season. A season total for game management lands near -50%,
  // which reads as losing half of every game instead of a couple of points a week,
  // and it cannot be compared between a coach with 21 games and one with 17.
  const perGame = playcalls ? c.epa_per_game : c.wp_impact_per_game_pct;
  const season = playcalls ? c.net_epa_vs_optimal : c.net_wp_impact_pct;
  const value = perGame ?? season;
  const unit = playcalls ? ' pts' : '%';
  const label = `${value > 0 ? '+' : ''}${value.toFixed(playcalls ? 2 : 2)}${unit}`;
  const optimal = c.decisions ? Math.round(((c.Optimal ?? 0) / c.decisions) * 100) : 0;
  return (
    <div className="card p-5">
      <p className="kicker">{title}</p>
      <p className="mt-0.5 text-[12px] text-muted">{what}</p>
      <div className="mt-3 flex items-end justify-between gap-3">
        <div><p className="num text-[30px] leading-none text-blue">{optimal}%</p><p className="label mt-1">optimal over {c.decisions} calls</p></div>
        <div className="text-right">
          <p className={`num text-[18px] ${value < 0 ? 'text-red' : 'text-green'}`}>{label}</p>
          <p className="label">{playcalls ? 'pts vs best call, per game' : 'win prob. per game'}</p>
          <p className="label mt-0.5 !text-faint">{`${season > 0 ? '+' : ''}${season.toFixed(1)}${unit} over ${c.games ?? 0}`}</p>
        </div>
      </div>
      <div className="mt-3 flex h-2 w-full overflow-hidden rounded-full bg-wash">
        {([['Optimal', 'bg-green'], ['Acceptable', 'bg-blue'], ['Questionable', 'bg-amber'], ['Wrong', 'bg-red']] as const).map(([k, cls]) => (
          <div key={k} className={cls} style={{ width: `${c.decisions ? ((c[k] ?? 0) / c.decisions) * 100 : 0}%` }} title={`${k}: ${c[k]}`} />
        ))}
      </div>
      <div className="mt-2 flex justify-between text-[11px] text-muted">
        {([['A', 'Optimal', 'text-green'], ['B', 'Acceptable', 'text-blue'], ['C', 'Questionable', 'text-amber'], ['F', 'Wrong', 'text-red']] as const).map(([l, k, color]) => <span key={k}><span className={`num ${color}`}>{l}</span> {c[k]}</span>)}
      </div>
    </div>
  );
}

type GameRow = { gid: string } & SeasonReport['per_game'][string];

export function CoachPage() {
  const { name = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const league = (params.get('league') === 'cfb' ? 'cfb' : 'nfl') as League;
  const seasonParam = params.get('season');
  const season = seasonParam ? Number(seasonParam) : undefined;
  const [report, setReport] = useState<SeasonReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Both cost no model call, so they load with the page rather than behind a click.
  // Neither is essential, so a failure leaves them out instead of failing the page.
  const [trend, setTrend] = useState<CoachTrend | null>(null);
  const [habits, setHabits] = useState<Tendencies | null>(null);
  const load = () => {
    setError(null); setReport(null);
    api.coachSeason(name, league, season).then(setReport).catch((e: Error) => setError(e.message));
    setTrend(null); setHabits(null);
    api.coachTrend(name, league).then(setTrend).catch(() => setTrend(null));
    api.coachTendencies(name, league).then(setHabits).catch(() => setHabits(null));
  };
  useEffect(() => { load(); }, [name, league, season]); // eslint-disable-line react-hooks/exhaustive-deps

  const games: GameRow[] = report ? Object.entries(report.per_game).map(([gid, c]) => ({ gid, ...c })) : [];
  const color = teamColor(report?.team_color);
  return (
    <>
      <section className="container pt-2 md:pt-4">
        <header className="relative overflow-hidden rounded-xl p-6 text-paper shadow-lift md:p-8" style={{ background: `linear-gradient(100deg, ${color}, ${color}dd)` }}>
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(600px_300px_at_20%_-20%,rgba(255,255,255,0.3),transparent_60%)]" aria-hidden="true" />
          <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              {report?.team_logo && <img src={logo(report.team_logo, 80)} alt="" className="h-16 w-16 object-contain drop-shadow md:h-20 md:w-20" />}
              <div>
                <p className="flex flex-wrap gap-x-3 text-[13px] text-paper/85"><span>{league === 'nfl' ? 'NFL' : 'College'}</span><Link to="/grades" className="underline-offset-4 hover:underline">All coaches</Link></p>
                <h1 className="h1 mt-1">{decodeURIComponent(name)}</h1>
                {/* his own calls first: a team total is mostly his coordinators' */}
                {report && <p className="mt-1 text-[15px] text-paper/90">{report.team_name ?? report.team}  {report.games} games  {report.phases.head_coach.decisions} of his own calls graded, {report.season.decisions} across the team</p>}
              </div>
            </div>
            {report?.seasons && report.seasons.length > 1 && (
              <select value={report.season_year ?? ''} onChange={(e) => setParams({ league, season: e.target.value })} className="h-11 rounded-full border border-white/40 bg-white/15 px-4 text-[13px] font-semibold text-paper outline-none backdrop-blur" aria-label="Season">
                {[...report.seasons].reverse().map((s) => <option key={s} value={s} className="text-ink">{s} season</option>)}
              </select>
            )}
          </div>
        </header>
      </section>
      <section className="section">
        <div className="container stack">
          {error && (error.toLowerCase().includes('no graded') || error.includes('404')
            ? <ErrorLine message="No graded season for that coach yet." />
            : <ErrorLine onRetry={load} />)}
          {!error && !report && <LoadingLine active />}
          {report && (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {PHASES.filter((p) => report.phases[p.id].decisions > 0).map((p) => <Card key={p.id} c={report.phases[p.id]} title={p.label} what={p.what} />)}
              </div>
              <Disclosure title="How to read these grades">
                <div className="grid grid-cols-1 gap-3 text-[14px] text-muted md:grid-cols-2">
                  <p><span className="font-semibold text-ink">Outcome-independent.</span> A call is graded against what usually works in that exact situation across every game in the data, not against how the play happened to turn out.</p>
                  <p><span className="font-semibold text-ink">Win probability cost.</span> For game management, the gap between the chosen option and the best option. The headline figure is per game, so -2.4% means his calls cost about two and a half points of win probability in an average game; the season total is underneath it.</p>
                  <p><span className="font-semibold text-ink">Points vs best call.</span> Play calls are priced in expected points because one play barely moves win probability. Negative is worse than the best available call.</p>
                  <p><span className="font-semibold text-ink">A, B, C, F.</span> Optimal, Acceptable, Questionable, Wrong. Thresholds are wide on purpose so only a clear, repeatable gap earns a C or an F.</p>
                  <p><span className="font-semibold text-ink">Whose call it was.</span> Only game management is the head coach's own. Public data does not name coordinators, so play calling, defense and special teams are the team's, and defensive calls in particular rarely separate: man and zone produce similar expected points in most situations, so nearly all of them grade out optimal.</p>
                </div>
              </Disclosure>
              {report.worst_decisions.length > 0 && (
                <div>
                  <p className="kicker mb-3">Costliest calls</p>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {report.worst_decisions.slice(0, 6).map((d, i) => <CoachGradeCard key={i} d={d} team={report.team ?? undefined} />)}
                  </div>
                </div>
              )}
              {(trend || habits) && (
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                  {trend && trend.seasons.length > 1 && <CoachTrendChart data={trend} />}
                  {habits && habits.decisions > 0 && <TendencyChart data={habits} />}
                </div>
              )}
              <div>
                <p className="kicker mb-3">By game</p>
                <DataTable rows={games} rowKey={(r) => r.gid}
                  mobile={(r) => (
                    <div className="card flex flex-col gap-3 p-4">
                      <div className="min-w-0"><p className="truncate text-[16px] font-semibold">{r.postseason ? 'Postseason' : r.week ? `Week ${r.week}` : r.gid}{r.opponent ? ` vs ${r.opponent}` : ''}</p><p className="meta !text-[11px]">{r.decisions} calls</p></div>
                      <div className="grid grid-cols-3 gap-3 rounded-lg bg-white/60 px-3 py-2"><Stat label="Optimal" value={`${((r.optimal_rate ?? 0) * 100).toFixed(0)}%`} tone="text-blue" /><Stat label="Wrong" value={r.Wrong} tone={r.Wrong ? 'text-red' : ''} /><Stat label="WP" value={`${r.net_wp_impact_pct > 0 ? '+' : ''}${r.net_wp_impact_pct.toFixed(1)}%`} tone={r.net_wp_impact_pct < 0 ? 'text-red' : ''} /></div>
                    </div>
                  )}
                  columns={[
                    { key: 'game', header: 'Game', minWidth: 200, render: (r) => <span className="font-medium">{r.postseason ? 'Postseason' : r.week ? `Week ${r.week}` : r.gid}{r.opponent ? ` vs ${r.opponent}` : ''}</span> },
                    { key: 'd', header: 'Calls', align: 'right', hideSm: true, render: (r) => r.decisions },
                    { key: 'o', header: 'Optimal', align: 'right', key_stat: true, render: (r) => `${((r.optimal_rate ?? 0) * 100).toFixed(0)}%` },
                    { key: 'w', header: 'Wrong', align: 'right', render: (r) => <span className={r.Wrong ? 'text-red' : ''}>{r.Wrong}</span> },
                    { key: 'wp', header: 'WP impact', align: 'right', render: (r) => <span className={r.net_wp_impact_pct < 0 ? 'text-red' : ''}>{r.net_wp_impact_pct > 0 ? '+' : ''}{r.net_wp_impact_pct.toFixed(1)}%</span> },
                  ]} />
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
